import React from 'react';
import { TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import parse, {
  DOMNode,
  Element,
  HTMLReactParserOptions,
  domToReact,
} from 'html-react-parser';
import {
  BulletItem,
  BulletList,
  MarkdownDocParagraph,
  MarkdownDocTable,
  MarkdownDocTitle,
  MarkdownInlineCode,
  SectionTitle,
} from '../../../../Markdown/styles';
import { getHeadingId } from '../../../../Markdown/utils';
import { DocumentationHtmlLink } from '../DocumentationHtmlLink';
import {
  DocumentationFeatureTitle,
  DocumentationHtmlBlockquote,
  DocumentationHtmlFigcaption,
  DocumentationHtmlFigure,
  DocumentationHtmlImage,
  DocumentationOrderedList,
} from '../htmlDocStyles';
import {
  getDomNodeText,
  getElementChildNodes,
  headingContainsAnchor,
  isElementName,
  isHtmlElement,
  isInsideFeatureHeading,
} from './documentationDomNodeUtils';
import { renderDocumentationCodeBlock } from './renderDocumentationCodeBlock';
import { renderGhostCallout } from './renderGhostCallout';

const headingLevel2Style = { marginTop: 24, marginBottom: 16 };
const headingLevel3PlusStyle = { marginTop: 24, marginBottom: 8 };
const documentationListItemStyle = { fontSize: 16, lineHeight: 1.5 };

interface DocumentationHtmlParserContext {
  assignHeadingId: (
    existingId: string | undefined,
    headingLabel: string,
  ) => string | undefined;
}

export function createDocumentationHeadingIdTracker() {
  const assignedHeadingIds = new Set<string>();

  function assignHeadingId(
    existingHeadingId: string | undefined,
    headingLabel: string,
  ): string | undefined {
    const generatedHeadingId = getHeadingId(headingLabel);
    let documentationHeadingId =
      existingHeadingId?.trim() || generatedHeadingId;

    if (!documentationHeadingId) {
      return undefined;
    }

    if (assignedHeadingIds.has(documentationHeadingId)) {
      let duplicateHeadingSuffix = 2;

      while (
        assignedHeadingIds.has(
          `${documentationHeadingId}-${duplicateHeadingSuffix}`,
        )
      ) {
        duplicateHeadingSuffix += 1;
      }

      documentationHeadingId = `${documentationHeadingId}-${duplicateHeadingSuffix}`;
    }

    assignedHeadingIds.add(documentationHeadingId);
    return documentationHeadingId;
  }

  return { assignHeadingId };
}

export function parseDocumentationHtml(
  html: string,
  parserContext: DocumentationHtmlParserContext,
) {
  return parse(html, createDocumentationHtmlParserOptions(parserContext));
}

function createDocumentationHtmlParserOptions(
  parserContext: DocumentationHtmlParserContext,
): HTMLReactParserOptions {
  const parserOptions: HTMLReactParserOptions = {};

  parserOptions.replace = (domNode: DOMNode) => {
    if (!isHtmlElement(domNode)) {
      return undefined;
    }

    const children = domToReact(
      getElementChildNodes(domNode),
      parserOptions,
    );

    switch (domNode.name) {
      case 'h1':
        return (
          <MarkdownDocTitle
            id={getDocumentationHeadingId(domNode, parserContext)}
          >
            {children}
          </MarkdownDocTitle>
        );
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        if (headingContainsAnchor(domNode)) {
          return (
            <DocumentationFeatureTitle
              id={getDocumentationHeadingId(domNode, parserContext)}
            >
              {children}
            </DocumentationFeatureTitle>
          );
        }

        if (domNode.name === 'h2') {
          return (
            <SectionTitle
              id={getDocumentationHeadingId(domNode, parserContext)}
              variant="h5"
              style={headingLevel2Style}
            >
              {children}
            </SectionTitle>
          );
        }

        return (
          <SectionTitle
            id={getDocumentationHeadingId(domNode, parserContext)}
            variant="h6"
            style={headingLevel3PlusStyle}
          >
            {children}
          </SectionTitle>
        );
      case 'p':
        return <MarkdownDocParagraph>{children}</MarkdownDocParagraph>;
      case 'ul':
        return <BulletList>{children}</BulletList>;
      case 'ol':
        return <DocumentationOrderedList>{children}</DocumentationOrderedList>;
      case 'li':
        return (
          <BulletItem style={documentationListItemStyle}>
            {children}
          </BulletItem>
        );
      case 'pre':
        return renderDocumentationCodeBlock(getDomNodeText(domNode));
      case 'code':
        if (isElementName(domNode.parent, 'pre')) {
          return undefined;
        }

        return (
          <MarkdownInlineCode>
            {getDomNodeText(domNode).trim()}
          </MarkdownInlineCode>
        );
      case 'blockquote':
        return (
          <DocumentationHtmlBlockquote>
            {children}
          </DocumentationHtmlBlockquote>
        );
      case 'table':
        return <MarkdownDocTable>{children}</MarkdownDocTable>;
      case 'thead':
        return <TableHead>{children}</TableHead>;
      case 'tbody':
        return <TableBody>{children}</TableBody>;
      case 'tr':
        return <TableRow>{children}</TableRow>;
      case 'th':
        return <TableCell variant="head">{children}</TableCell>;
      case 'td':
        return <TableCell>{children}</TableCell>;
      case 'img':
        return (
          <DocumentationHtmlImage
            src={domNode.attribs?.src}
            alt={domNode.attribs?.alt ?? ''}
            title={domNode.attribs?.title}
          />
        );
      case 'figure':
        return <DocumentationHtmlFigure>{children}</DocumentationHtmlFigure>;
      case 'figcaption':
        return (
          <DocumentationHtmlFigcaption>
            {children}
          </DocumentationHtmlFigcaption>
        );
      case 'a':
        return (
          <DocumentationHtmlLink
            href={domNode.attribs?.href}
            variant={isInsideFeatureHeading(domNode) ? 'feature' : 'body'}
          >
            {children}
          </DocumentationHtmlLink>
        );
      default:
        break;
    }

    const className = domNode.attribs?.class ?? '';
    if (className.includes('kg-callout-card')) {
      return renderGhostCallout(domNode, parserOptions);
    }

    return undefined;
  };

  return parserOptions;
}

function getDocumentationHeadingId(
  domNode: Element,
  parserContext: DocumentationHtmlParserContext,
): string | undefined {
  const headingLabel = getDomNodeText(domNode).trim();

  return (
    parserContext.assignHeadingId(domNode.attribs?.id, headingLabel) ||
    getHeadingId(headingLabel)
  );
}
